import styled, { css } from 'styled-components';

import { useClickAttachment } from 'hooks/useClickAttachment';
import { SummaryAttachment } from 'models/aiArtifacts/Summary';
import colors from 'styles/theme/colors';

export type CitationBadgeVariant = 'purple' | 'white';

type CitationBadgeProps = {
  attachment: SummaryAttachment;
  size?: number;
  // using a negative because most components will want to show the hover effect
  hideHoverEffect?: boolean;
  clickable?: boolean;
  onClick?: () => void;
};

export default function CitationBadge({
  attachment,
  size = 16,
  hideHoverEffect = false,
  clickable = true,
  onClick,
}: CitationBadgeProps) {
  const openAttachment = useClickAttachment(attachment.attachmentId);

  const handleClick = onClick ?? openAttachment;

  return (
    <StyledCitationBadge
      size={size}
      hoverable={!hideHoverEffect}
      clickable={clickable || !!onClick}
      variant='purple'
      onClick={clickable || !!onClick ? handleClick : undefined}
      title={!hideHoverEffect ? attachment.filename : undefined}>
      {attachment.displayId}
    </StyledCitationBadge>
  );
}

const purpleStyles = css`
  background: ${colors.aiPurpleLight20};
  color: #3e2d6e;
  border-radius: 26px;
`;

const whiteStyles = css`
  background: ${colors.white};
  color: ${colors.black};
  border: 0.5px solid ${colors.black25};
  border-radius: 26px;

  &:hover {
    background: ${colors.primaryBlue05};
    border-color: ${colors.primaryBlue};
  }
`;

export const StyledCitationBadge = styled.span<{
  size: number;
  hoverable: boolean;
  clickable: boolean;
  variant: CitationBadgeVariant;
}>`
  width: ${({ size }) => size}px;
  height: ${({ size }) => size}px;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 8px;
  font-size: 8px;
  font-weight: 700;
  cursor: ${({ clickable }) => (clickable ? 'pointer' : 'default')};
  ${({ variant }) => (variant === 'white' ? whiteStyles : purpleStyles)}
  ${({ hoverable, variant }) =>
    hoverable &&
    variant === 'purple' &&
    css`
      &:hover {
        border: 0.5px solid #3e2d6e;
      }
    `}
`;
